import { useState, useMemo, useEffect } from 'react';
import { looksLikeBarcode, normalizeBarcode } from '@pantry/shared/utils';
import type { ProductSearchParams, ProductSearchResult } from '../types';
import { useSearchProducts } from './useSearchProducts';

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

interface UseProductSearchWithDebounceOptions {
  householdId: string;
  barcodeOverride?: string;
  enabled?: boolean;
}

interface UseProductSearchWithDebounceResult {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  debouncedTerm: string;
  isBarcode: boolean;
  results: ProductSearchResult | undefined;
  isLoading: boolean;
}

/**
 * Consolidated product search hook with debounce and barcode detection.
 * Manages search term state, debounces input, detects barcodes for faster
 * debounce timing, and calls useSearchProducts.
 *
 * @example
 * const { searchTerm, setSearchTerm, results, isLoading } = useProductSearchWithDebounce({
 *   householdId: '123',
 * });
 */
export function useProductSearchWithDebounce(
  options: UseProductSearchWithDebounceOptions
): UseProductSearchWithDebounceResult {
  const { householdId, barcodeOverride, enabled = true } = options;
  const [searchTerm, setSearchTerm] = useState('');

  const isBarcode = looksLikeBarcode(searchTerm);
  const debouncedTerm = useDebouncedValue(searchTerm, isBarcode ? 100 : 300);

  const searchParams: ProductSearchParams = useMemo(() => {
    if (barcodeOverride) {
      return { barcode: normalizeBarcode(barcodeOverride), includeExternal: true, limit: 10 };
    }
    if (looksLikeBarcode(debouncedTerm)) {
      return { barcode: normalizeBarcode(debouncedTerm), includeExternal: true, limit: 10 };
    }
    return { q: debouncedTerm || undefined, includeExternal: true, limit: 10 };
  }, [debouncedTerm, barcodeOverride]);

  const { data: results, isFetching } = useSearchProducts(householdId, searchParams, {
    enabled,
  });

  return {
    searchTerm,
    setSearchTerm,
    debouncedTerm,
    isBarcode,
    results,
    isLoading: isFetching,
  };
}
